// API Client
import { post } from "../CarbonClient";
import { DashboardEmissionBudgetType, DashboardMappingType, DashboardUnitType } from "@/features/Dashboard";
import * as Sentry from "@sentry/vue";

// API Path
const path = "api/dashboard/";

// Get dashboard
async function getDashboard(id: string, data: any) {
  return post(`${path}${id}`, data);
}

export type DashboardSummaryRequest = {
  indicatorTypeMappingId?: string;
  models?: string[];
  stages?: string[];
  /**
   * Pins the summary to a revision's saved state (as-of read); absent = the working state. This
   * response feeds the Transport toolbar's "% MAPPED" bar and its emission figure, so without it
   * a preview showed the working state's numbers under a frozen revision's name. Resolve it at
   * call time from readPinRevisionIdFor — never from a value captured earlier.
   *
   * The navigation mapping-lock read (useProjectMappingStatus) deliberately leaves it out: it
   * asks whether the project has ever been mapped, which is not a per-revision question.
   */
  revisionId?: string;
};

export type DashboardSummaryResponse = {
  emissionBudget: DashboardEmissionBudgetType;
  mapping: DashboardMappingType;
  unit: DashboardUnitType;
};

async function getDashboardSummary(id: string, data: DashboardSummaryRequest): Promise<DashboardSummaryResponse> {
  return post(`${path}${id}/summary`, data);
}

export type GetEmissionOverTimeRequest = {
  indicatorTypeMappingId?: string;
  models?: string[];
  stages?: string[];
};

export type AnnualEmissions = {
  year: number;
  embodiedEmissions: number;
  embodiedEmissionsWithModuleD: number;
  operationalEmissions: number;
  hasReplacement: boolean;
};

export type GetEmissionOverTimeResponse = {
  items: AnnualEmissions[];
};

async function getEmissionOverTime(
  id: string,
  payload: GetEmissionOverTimeRequest
): Promise<GetEmissionOverTimeResponse | undefined> {
  try {
    const response = await post(`${path}${id}/emissions-over-time`, {
      ...payload,
      models: payload.models?.length ?? 0 > 0 ? payload.models : undefined,
      stages: payload.stages?.length ?? 0 > 0 ? payload.stages : undefined,
    });

    if ("status" in response) {
      return undefined;
    }

    return response;
  } catch (error) {
    Sentry.captureException(error);
  }

  return undefined;
}

export { getDashboard, getDashboardSummary, getEmissionOverTime };
